import { createContext, ReactNode, useState } from 'react';

import { Volume } from '~/shared/types/api';

export interface DatafySwitchVolume {
  volumeId: Volume['volumeId'];
  state: 'datafying' | 'undatafying';
  timeStamp: Date;
}

export type DatafySwitchContextType = {
  datafySwitchVolumes: DatafySwitchVolume[];
  addDatafySwitchVolume: (
    volumeId: DatafySwitchVolume['volumeId'],
    state: DatafySwitchVolume['state']
  ) => void;
  toggleDatafySwitchVolumeState: (volumeId: DatafySwitchVolume['volumeId']) => void;
  removeDatafySwitchVolume: (volumeId: DatafySwitchVolume['volumeId']) => void;
};

export const DatafySwitchContext = createContext<DatafySwitchContextType>(null!);

export const DatafySwitchProvider = ({ children }: { children: ReactNode }) => {
  const [datafySwitchVolumes, setDatafySwitchVolumes] = useState<DatafySwitchVolume[]>([]);

  function addDatafySwitchVolume(
    volumeId: DatafySwitchVolume['volumeId'],
    state: DatafySwitchVolume['state']
  ) {
    setDatafySwitchVolumes((prev) => [...prev, { volumeId, state, timeStamp: new Date() }]);
  }

  function toggleDatafySwitchVolumeState(volumeId: DatafySwitchVolume['volumeId']) {
    setDatafySwitchVolumes((prev) => {
      const idx = prev.findIndex((v) => v.volumeId === volumeId);
      if (idx === -1) {
        return prev;
      }
      const next = [...prev];
      const state: DatafySwitchVolume['state'] =
        next[idx].state === 'datafying' ? 'undatafying' : 'datafying';
      next[idx] = { volumeId, state, timeStamp: new Date() };
      return next;
    });
  }

  function removeDatafySwitchVolume(volumeId: DatafySwitchVolume['volumeId']) {
    setDatafySwitchVolumes((prev) => prev.filter((volume) => volume.volumeId !== volumeId));
  }

  return (
    <DatafySwitchContext.Provider
      value={{
        datafySwitchVolumes,
        addDatafySwitchVolume,
        toggleDatafySwitchVolumeState,
        removeDatafySwitchVolume,
      }}
    >
      {children}
    </DatafySwitchContext.Provider>
  );
};

export default DatafySwitchProvider;
